// Badges for the things that are not pieces: a gun, a warehouse, a power's
// address.

import type { GameState } from "src/engine/rules/commands";
import {
  ALTAR,
  KEYSTONE,
  SHIELD_GENERATOR,
  type Structure,
  TRAIN_ENGINE
} from "src/engine/rules/industry/state";
import { ammoOf, fieldSquares, gunOf, newTurret, reachFor, shotsFor, turretEmplaced } from "src/engine/rules/industry/turret";
import { lateArtifactPips } from "./lateArtifacts";
import { hasPower, POWER_KIND, POWER_TABLE, POWERS, targetingArrays } from "src/engine/rules/campaign/powers";

import { squareLabel } from "src/engine/session/gridLabels";
import { STRUCTURE_GLYPH } from "../glyphs";
import {
  stockByGrade,
  stockBySlot,
  STRUCTURE_NAMES,
  structureHealthOf,
  structureMaxHealth
} from "src/engine/rules/industry/structures";
import { DEFLECTOR, GRADE_NAMES, inventoryOf, PIECE_NAMES, TRAINS } from "src/engine/rules/rules";
import { DIRECTION_NAMES } from "src/shared/board";
import { type MaterialLine, materialName } from "src/engine/rules/materials";
import { pieceType, type PieceType } from "src/engine/rules/board";
import type { BadgePip, PieceBadge } from "./types";

/**
 * A turret, for the shelf (§31 S8): its gun, its round (shots, fuel,
 * ammunition), its facing, and the field it covers now.
 */
export function turretDetail(state: GameState, sq: number, structure: Structure): PieceBadge {
  const turret = structure.turret ?? newTurret(structure.kind, structure.owner);
  const gun = gunOf(structure.kind);
  const arrays = targetingArrays(state.industry, structure.owner);
  const ammo = ammoOf(structure);
  const field = fieldSquares(state.position, sq, structure, arrays);
  const aims = gun.shape === "cone";
  const reach = reachFor(structure.kind, arrays);
  const pips: BadgePip[] = [
    {
      glyph: "◎",
      label:
        `${gun.damage} damage, ${shotsFor(structure.kind, arrays)} a round, ` +
        (aims ? `${reach}-deep cone facing ${DIRECTION_NAMES[turret.facing]}` : `radius ${reach}`),
      tone: "working"
    },
    {
      glyph: "⚡",
      label: `${turret.shots} shot(s) left this round · ${turret.fuel} fuel banked`,
      tone: turret.fuel > 0 ? "ready" : "waiting"
    }
  ];
  if (gun.ammo) {
    pips.push({
      glyph: "•",
      label: `${ammo} of ${inventoryOf(structure.kind)} ${materialName(gun.ammo.line as MaterialLine, gun.ammo.grade).toLowerCase()} loaded`,
      tone: ammo > 0 ? "ready" : "waiting"
    });
  }
  if (turretEmplaced(structure)) {
    pips.push({ glyph: "█", label: "Emplaced on a Reinforced Wall: immune to ranged fire", tone: "working" });
  }

  return {
    square: sq,
    owner: structure.owner,
    kind: "turret",
    short: `${turret.shots}`,
    title: `${STRUCTURE_NAMES[structure.kind]} · ${squareLabel(sq)}`,
    detail:
      `Fires at enemies in its field at your End Turn; shots it did not ` +
      `spend are overwatch, fired at enemies moving into its field until ` +
      `your next turn. Fed fuel by your power network` +
      (gun.ammo ? `, ammunition by your belts.` : `.`) +
      (aims ? ` Turning it costs a move.` : ``),
    progress: gun.ammo ? Math.min(1, ammo / inventoryOf(structure.kind)) : 0,
    stock: stockByGrade(structure),
    capacity: gun.ammo ? inventoryOf(structure.kind) : 0,
    accept: structure.accept,
    offer: structure.offer,
    turret: { state: turret, aims, ammo, field },
    pips,
    materials: stockBySlot(structure)
      .map((count, slot) => ({ slot, count }))
      .filter((entry) => entry.count > 0)
      .reverse(),
    actions: []
  };
}

/**
 * "This building has been hit", or null while it is whole (§27).
 *
 * A building takes ten and the largest blow in the game is nine, so the first
 * swing at one never takes it — which makes the count the only thing that says
 * how many more it can stand, and the player's whole cue to defend it.
 */
export function damagePip(structure: Structure): BadgePip | null {
  const whole = structureMaxHealth(structure);
  const left = structureHealthOf(structure);
  if (left >= whole) return null;
  return {
    glyph: "✚",
    label: `Damaged — ${left} of ${whole} health left`,
    tone: "waiting"
  };
}


/**
 * An artifact structure, for the shelf (§26 F6).
 *
 * What it says is the one thing a warehouse's panel cannot: which power this
 * is the address of, and whether it is on — which is only ever "yes" while it
 * stands under its owner's flag, so the panel doubles as the sentence that
 * teaches the rule. No stock, no capacity bar, no taps. Taking your own down
 * is legal and lossy on purpose, and the rail's Discard tool is how.
 */
export function artifactDetail(state: GameState, sq: number, structure: Structure): PieceBadge {
  const power = POWERS.find((id) => POWER_KIND[id] === structure.kind);
  const on = power ? hasPower(state.industry, structure.owner, power) : false;
  // **The altar holds stock, alone among the artifact kinds** (§26 A1) — the
  // sacrifice's yield and the capture bonus both land in it, and a panel
  // silent about five crucible steel is the mechanic reading as broken.
  // Reported from play. The taps stay absent: it takes no deliveries and its
  // offer is not a setting, so there is no matrix to draw.
  const furnace = structure.kind === ALTAR;
  const held = structure.stock.length;

  return {
    square: sq,
    owner: structure.owner,
    kind: "artifact",
    short: STRUCTURE_GLYPH[structure.kind],
    title: `${STRUCTURE_NAMES[structure.kind]} · ${squareLabel(sq)}`,
    detail: furnace
      ? `Carries Sacrifice: on while this stands under its owner's flag, and ` +
        `off the moment it falls. Bodies melted beside it — and enemies ` +
        `captured beside it — land here as refined metal, and it hands the ` +
        `pile on by itself to a machine standing beside it (never diagonally) ` +
        `or a belt pointing away. It takes no deliveries. This square is not ` +
        `board any more: nothing moves onto it or slides through it, and an ` +
        `enemy piece stepping here captures it.`
      : power
        ? `Carries ${POWER_TABLE[power].name}: on while this stands under its ` +
          `owner's flag, and off the moment it falls. It holds no stock and ` +
          `takes no deliveries — the power is the whole of what it does. This ` +
          `square is not board any more: nothing moves onto it or slides ` +
          `through it, and an enemy piece stepping here captures it.`
        : `Carries no power yet. It stands, it can be captured, and what it ` +
          `will do arrives with the scepter. This square is not board any ` +
          `more: nothing moves onto it or slides through it.`,
    pips: [
      {
        glyph: STRUCTURE_GLYPH[structure.kind],
        label: power
          ? `${POWER_TABLE[power].name} — ${on ? "on while it stands" : "off"}`
          : "No power yet",
        tone: on ? "working" : "waiting"
      },
      // A Shield Generator's round (§31 A9): lit or dark, and this turn's feed.
      ...(structure.kind === SHIELD_GENERATOR
        ? [
            {
              glyph: "⛨",
              label:
                `${structure.lit ? "Lit this round" : "Dark"} — fed ${structure.fed ?? 0} of ` +
                `${DEFLECTOR.fuelPerRound} fuel points this turn`,
              tone: (structure.lit ? "working" : "waiting") as "working" | "waiting"
            }
          ]
        : []),
      // Stoke the Forges, My Deck Runith Over, Yield Not, Flow Faster (§31 A10–A13).
      ...lateArtifactPips(state, sq, structure, on),
      // The Targeting Array's stack (§31 A7): S8's turrets grow with it.
      ...(structure.kind === KEYSTONE
        ? [
            {
              glyph: "⌖",
              label: `${targetingArrays(state.industry, structure.owner)} Targeting Array(s) powered`,
              tone: (on ? "working" : "waiting") as "working" | "waiting"
            }
          ]
        : []),
      ...(furnace
        ? [
            {
              glyph: "▣",
              label:
                held > 0
                  ? `Holding ${held} of ${inventoryOf(structure.kind)} — hands on to a ` +
                    `machine or belt beside it`
                  : "Empty — sacrifices and captures beside it land here",
              tone: (held > 0 ? "working" : "waiting") as "working" | "waiting"
            }
          ]
        : [])
    ],
    materials: furnace
      ? stockBySlot(structure)
          .map((count, slot) => ({ slot, count }))
          .filter((entry) => entry.count > 0)
          .reverse()
      : [],
    actions: []
  };
}

/**
 * A building, for the shelf.
 *
 * It is not a piece — it has no type, it is not in `counts`, and its square is
 * not even on the board — but the shelf's three columns describe it perfectly
 * well: what it is, what it holds, what you can do. Reusing the badge shape
 * rather than inventing a second one keeps that promise: a rook and a store
 * differ in what the columns contain, never in where they are.
 */
export function storeDetail(state: GameState, sq: number, structure: Structure): PieceBadge {
  const held = structure.stock.length;
  const byGrade = stockByGrade(structure);
  // No inventory (§33 I2: walls, the power network, track): no count and no
  // taps, or the panel reads "0 of 0" over controls the reducer refuses.
  if (inventoryOf(structure.kind) === 0) {
    return {
      square: sq,
      owner: structure.owner,
      kind: "store",
      short: STRUCTURE_GLYPH[structure.kind],
      title: `${STRUCTURE_NAMES[structure.kind]} · ${squareLabel(sq)}`,
      detail: "Holds no material.",
      progress: 0,
      stock: byGrade,
      capacity: 0,
      pips: [],
      materials: [],
      actions: []
    };
  }

  return {
    square: sq,
    owner: structure.owner,
    kind: "store",
    short: `${held}/${inventoryOf(structure.kind)}`,
    title: `${STRUCTURE_NAMES[structure.kind]} · ${squareLabel(sq)}`,
    detail:
      `Holds ${held} of ${inventoryOf(structure.kind)} units, across every grade — ` +
      `a total rather than four bins, so a flood of one metal crowds out ` +
      `another unless you close its tap. This square is not board any more: ` +
      `nothing moves onto it or slides through it.`,
    progress: held / inventoryOf(structure.kind),
    stock: byGrade,
    capacity: inventoryOf(structure.kind),
    accept: structure.accept,
    offer: structure.offer,
    // The taps, not the numbers (PLAN §14.8). What a player needs from across
    // the board is which grades are open and in which direction; the amounts
    // are in the shelf, a click away.
    pips: [
      {
        glyph: "▣",
        label: `${STRUCTURE_NAMES[structure.kind]}, holding ${held} of ${inventoryOf(structure.kind)}`,
        tone: held > 0 ? "working" : "waiting"
      },
      ...tapPips(structure)
    ],
    materials: stockBySlot(structure)
      .map((count, slot) => ({ slot, count }))
      .filter((entry) => entry.count > 0)
      .reverse(),
    actions: []
  };
}

/**
 * A store's taps, as one pip per direction naming the grades it is open to.
 *
 * Two pips rather than eight: "takes iron and silver" is the sentence, and a
 * pip per grade per direction would be the arrow soup PLAN §7.1 rates the
 * number one risk. A direction with nothing open says nothing at all — a
 * sealed store is quiet, which is what sealed should look like.
 */
function tapPips(structure: Structure): BadgePip[] {
  const names = (mask: number): string =>
    GRADE_NAMES.filter((_, grade) => (mask & (1 << grade)) !== 0).join(", ");

  const pips: BadgePip[] = [];
  if (structure.accept !== 0) {
    pips.push({ glyph: "→", label: `Takes ${names(structure.accept)}`, tone: "ready" });
  }
  if (structure.offer !== 0) {
    pips.push({ glyph: "←", label: `Gives out ${names(structure.offer)}`, tone: "working" });
  }
  return pips;
}

/**
 * A Train Engine or Car (§31 S7), for the shelf: its order and coal, or its
 * riders. Always a badge — a train's state is what it is doing.
 */
export function trainDetail(sq: number, structure: Structure): PieceBadge {
  const train = structure.train ?? {};
  const engine = structure.kind === TRAIN_ENGINE;
  const coal = structure.stock.length;
  const riders = train.riders ?? [];
  const dest = train.dest ?? -1;
  return {
    square: sq,
    owner: structure.owner,
    kind: "artifact",
    short: engine ? (dest >= 0 ? "→" : STRUCTURE_GLYPH[structure.kind]) : `${riders.length}/${TRAINS.carRiders}`,
    title: `${STRUCTURE_NAMES[structure.kind]} · ${squareLabel(sq)}`,
    detail: engine
      ? `Runs forward along your track to where you send it: select it, then click track. ` +
        `Burns ${TRAINS.coalPerPoint} coal a tick for up to ${TRAINS.squaresPerPoint} squares; ` +
        `holds ${coal} of ${inventoryOf(structure.kind)} coal and pulls ${(train.cars ?? []).length} of ${TRAINS.maxCars} cars.`
      : `Carries ${riders.length} of ${TRAINS.carRiders}: select a piece beside it and click the car to board, ` +
        `select the car and click a square straight beside it to set a rider down — two each way a turn.`,
    pips: [
      engine
        ? {
            glyph: STRUCTURE_GLYPH[structure.kind],
            label: dest >= 0 ? `Driving to ${squareLabel(dest)}` : "No order",
            tone: dest >= 0 ? "working" : "waiting"
          }
        : {
            glyph: STRUCTURE_GLYPH[structure.kind],
            label:
              riders.length > 0
                ? `Carrying ${riders.map((one) => PIECE_NAMES[pieceType(one.piece) as PieceType].toLowerCase()).join(", ")}`
                : "Empty",
            tone: riders.length > 0 ? "working" : "waiting"
          }
    ],
    materials: [],
    actions: []
  };
}
